import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import NewsCard from "@/components/feature/NewsCard";
import FilterBar from "./components/FilterBar";
import Pagination from "./components/Pagination";
import useSeo from "@/hooks/useSeo";
import {
  PAGE_SIZE,
  allArticles,
  categoryList,
  filterArticles,
  getCategory,
  regionOptions,
} from "@/utils/articles";

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = getCategory(categoryName);

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const regionKey = selectedRegions.join(",");

  useSeo({
    title: category
      ? `${category.name} - 51黑料网爆料专区`
      : "栏目不存在 - 51黑料网",
    description: category
      ? `${category.description} 51黑料网${category.name}频道，持续更新最新爆料、背景资料与多方回应。`
      : "该栏目不存在或已调整，请返回51黑料网首页浏览最新爆料资讯。",
    keywords: category ? category.keywords : "51黑料网,爆料栏目",
  });

  useEffect(() => {
    setLoading(true);
    setPage(1);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [sort, regionKey]);

  const filtered = useMemo(
    () => filterArticles({ category: category?.slug, regions: selectedRegions, sort }),
    [category?.slug, regionKey, sort, selectedRegions]
  );

  const categoryTotal = allArticles.filter((item) => item.category === category?.slug).length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleRegion = (region: string) => {
    setSelectedRegions((prev) =>
      prev.includes(region) ? prev.filter((item) => item !== region) : [...prev, region]
    );
  };

  const clearFilters = () => {
    setSelectedRegions([]);
    setSort("latest");
  };

  const changePage = (nextPage: number) => {
    setPage(nextPage);
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1">
          <div className="max-w-portal mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-folder-unknow-line text-2xl" aria-hidden="true" />
            </span>
            <h1 className="font-heading text-2xl font-black text-foreground-950">栏目不存在或已调整</h1>
            <p className="mt-2 text-sm text-foreground-500">
              你访问的分类可能已经改名，可以从下面的栏目继续浏览。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              {categoryList.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="h-9 px-4 inline-flex items-center rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="max-w-portal mx-auto px-4 md:px-6">
          <nav className="py-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors">
              {category.name}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-800">{category.name}列表</span>
          </nav>

          <header className="rounded-lg bg-background-100 border border-background-200 px-5 py-6 md:px-7 md:py-8 relative overflow-hidden">
            <p className="text-[11px] font-label tracking-[0.24em] text-primary-600 mb-2">
              {category.slug.toUpperCase()} CHANNEL
            </p>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
              {category.name}爆料专区
            </h1>
            <p className="mt-2.5 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {category.description}
            </p>
            <p className="mt-3 text-xs text-foreground-500">
              共收录 <span className="font-label font-bold text-primary-700">{categoryTotal}</span> 条爆料 ·
              持续更新中
            </p>
            <span className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 font-heading text-[68px] md:text-[110px] font-black text-background-300/60 select-none pointer-events-none hidden sm:block">
              {category.navLabel}
            </span>
          </header>

          <div className="mt-5">
            <FilterBar
              sort={sort}
              onSortChange={setSort}
              selectedRegions={selectedRegions}
              onToggleRegion={toggleRegion}
              onClear={clearFilters}
              regionOptions={regionOptions}
              resultCount={filtered.length}
            />
          </div>

          <div ref={resultsRef} className="mt-5 scroll-mt-24">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">
                {loading ? (
                  "正在更新列表…"
                ) : (
                  <>
                    匹配到 <span className="font-label font-bold text-primary-700">{filtered.length}</span> 条内容
                  </>
                )}
              </p>
              <p className="text-xs text-foreground-400 font-label">
                PAGE {safePage} / {totalPages}
              </p>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 flex gap-4 animate-pulse"
                  >
                    <div className="w-[118px] h-[88px] md:w-[200px] md:h-[130px] rounded-md bg-background-200 shrink-0" />
                    <div className="flex-1 space-y-3 py-1">
                      <div className="h-3 w-24 rounded bg-background-200" />
                      <div className="h-4 w-3/4 rounded bg-background-200" />
                      <div className="h-3 w-full rounded bg-background-100" />
                      <div className="h-3 w-1/2 rounded bg-background-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : pagedItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-3">
                  <i className="ri-search-eye-line text-xl" aria-hidden="true" />
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-900">暂无符合条件的黑料</h2>
                <p className="mt-1.5 text-sm text-foreground-500">
                  试试减少地域筛选，或切换到“最新发布”看看。
                </p>
                <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full sm:w-auto h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清空筛选条件
                  </button>
                  <Link
                    to="/"
                    className="w-full sm:w-auto h-10 px-4 inline-flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <NewsCard article={pagedItems[0]} variant="list" />
                {pagedItems.length > 1 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {pagedItems.slice(1).map((article) => (
                      <NewsCard key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {!loading && pagedItems.length > 0 && (
              <Pagination currentPage={safePage} totalPages={totalPages} onChange={changePage} />
            )}
          </div>

          <section className="mt-10 rounded-lg bg-primary-700 text-background-50 px-5 py-6 md:px-7 md:py-7 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-label tracking-[0.2em] text-accent-300 mb-1.5">
                TIP US
              </p>
              <h2 className="font-heading text-xl md:text-2xl font-black">有{category.name}的线索？</h2>
              <p className="mt-1.5 text-sm text-background-50/80">
                接受匿名投稿，我们会先核对时间线与公开信息，再决定是否发布。
              </p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line" aria-hidden="true" />
              </span>
              我要爆料
            </Link>
          </section>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}